import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';     
import { FaChevronDown } from 'react-icons/fa';
import { logoutAsync } from '../../auth/authSlice';

const WholesalerNavbar = () => {
  const { shopName } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const handleLogout = async () => {
    const confirmed = window.confirm('Are you sure you want to logout?');
    if (confirmed) {
      try {
        await dispatch(logoutAsync()).unwrap();
        navigate('/login');
      } catch (error) {
        console.error('Logout failed:', error);
      }
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  // Function to check if a link is active
  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Common styles for active and inactive states
  const getLinkStyles = (path) => {
    const baseStyles = "px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200";
    return isActive(path)
      ? `${baseStyles} bg-yellow-500 text-white shadow-sm`
      : `${baseStyles} text-gray-600 hover:text-gray-900 hover:bg-gray-50`;
  };

  // Common styles for mobile menu items
  const getMobileLinkStyles = (path) => {
    const baseStyles = "block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200";
    return isActive(path)
      ? `${baseStyles} bg-yellow-500 text-white shadow-sm`
      : `${baseStyles} text-gray-700 hover:text-gray-900 hover:bg-gray-50`;
  };

  // Dropdown menu component
  const DropdownMenu = ({ items, isMobile = false }) => {
    const baseStyles = isMobile ? "pl-4" : "absolute mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5";
    return (
      <div className={baseStyles}>
        <div className="py-1" role="menu" aria-orientation="vertical">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${
                isActive(item.path) ? 'bg-yellow-50 text-yellow-700' : 'text-gray-700 hover:bg-gray-50'
              } block px-4 py-2 text-sm ${isMobile ? 'pl-8' : ''}`}
              onClick={() => {
                setIsMenuOpen(false);
                setActiveDropdown(null);
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  const orderItems = [
    { path: '/wholesaler/orders/requests', label: 'Order Requests' },
    { path: '/wholesaler/orders/approved', label: 'Approved Orders' }
  ];

  const connectionItems = [
    { label: 'Add Connection', path: '/wholesaler/connections/add' },
    { label: 'Requests', path: '/wholesaler/connections/requests' },
    { label: 'Approved', path: '/wholesaler/connections/approved' }
  ];

  return (
    <nav className="bg-white shadow-md fixed w-full top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Company Name */}
          <div className="flex items-center">
            <Link to="/wholesaler/inventory" className="flex items-center">
              <svg 
                className="h-8 w-8 text-yellow-500" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 100-12 6 6 0 000 12zm-1-5a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" 
                  clipRule="evenodd" 
                />
              </svg>
              <span className="ml-2 text-xl font-semibold text-gray-800">
                {shopName || 'SwarnBhandar'}
              </span>
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-2">
            <Link 
              to="/wholesaler/inventory" 
              className={getLinkStyles('/wholesaler/inventory')}
            >
              Inventory
            </Link>
            
            {/* Orders Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('orders')}
                className={`${getLinkStyles('/wholesaler/orders')} flex items-center`}
              >
                Orders
                <FaChevronDown className="ml-1 h-3 w-3" />
              </button>
              {activeDropdown === 'orders' && (
                <DropdownMenu items={orderItems} />
              )}
            </div>

            {/* Connections Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('connections')}
                className={`${getLinkStyles('/wholesaler/connections')} flex items-center`}
              >
                Connections
                <FaChevronDown className="ml-1 h-3 w-3" />
              </button>
              {activeDropdown === 'connections' && (
                <DropdownMenu items={connectionItems} />
              )}
            </div>

            <Link 
              to="/wholesaler/profile" 
              className={getLinkStyles('/wholesaler/profile')}
            >
              Profile
            </Link>
            <button
              onClick={handleLogout}
              className="ml-4 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
            >
              Logout
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-yellow-500"
            >
              <span className="sr-only">Open main menu</span>
              {/* Icon when menu is closed */}
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {/* Icon when menu is open */}
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <Link
            to="/wholesaler/inventory"
            className={getMobileLinkStyles('/wholesaler/inventory')}
            onClick={() => setIsMenuOpen(false)}
          >
            Inventory
          </Link>

          {/* Mobile Orders Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('orders-mobile')}
              className={`${getMobileLinkStyles('/wholesaler/orders')} w-full flex justify-between items-center`}
            >
              Orders
              <FaChevronDown className="h-3 w-3" />
            </button>
            {activeDropdown === 'orders-mobile' && (
              <DropdownMenu items={orderItems} isMobile={true} />
            )}
          </div>

          {/* Mobile Connections Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('connections-mobile')}
              className={`${getMobileLinkStyles('/wholesaler/connections')} w-full flex justify-between items-center`}
            >
              Connections
              <FaChevronDown className="h-3 w-3" />
            </button>
            {activeDropdown === 'connections-mobile' && (
              <DropdownMenu items={connectionItems} isMobile={true} />
            )}
          </div>

          <Link
            to="/wholesaler/profile"
            className={getMobileLinkStyles('/wholesaler/profile')}
            onClick={() => setIsMenuOpen(false)}
          >
            Profile
          </Link>
          <button
            onClick={() => {
              handleLogout();
              setIsMenuOpen(false);
            }}
            className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
};

export default WholesalerNavbar; 